import { formatDate } from "../../api/dates";
import { formatMoney, sumMoney } from "../../api/money";
import { GROSS_PROFIT, SITE_NAME, WORKS_ORDER, statusLabel, type ColumnDef } from "./columns";

/** One report row keyed by workbook header, so a cell is found by name and
 * never by a position that shifts when the server strips the money. */
export type TrackerRecord = Readonly<Record<string, string | null>>;

export function toRecords(columns: readonly string[], rows: readonly (string | null)[][]): TrackerRecord[] {
  return rows.map((row) => Object.fromEntries(columns.map((column, index) => [column, row[index] ?? null])));
}

function cellText(column: ColumnDef, value: string | null): string {
  if (value === null || value === "") {
    return "—";
  }
  if (column.kind === "money") {
    return formatMoney(value);
  }
  if (column.kind === "date") {
    return formatDate(value);
  }
  if (column.kind === "status") {
    return statusLabel(value);
  }
  return value;
}

function cellClass(column: ColumnDef, value: string | null): string | undefined {
  const empty = value === null || value === "";
  const margin = column.key === GROSS_PROFIT && !empty ? Number(value) : 0;
  const names = [
    column.kind === "money" ? "num" : "",
    empty ? "empty" : "",
    margin > 0 ? "profit" : "",
    margin < 0 ? "loss" : "",
  ].filter((name) => name !== "");
  return names.length === 0 ? undefined : names.join(" ");
}

/** The report as a table that scrolls both ways inside its card: the site
 * stays pinned on the left, the headings on top and the totals at the
 * bottom, so a row far to the right or far down still says whose it is. */
export function TrackerTable({ records, columns }: { records: readonly TrackerRecord[]; columns: readonly ColumnDef[] }) {
  return (
    <div className="table-card tracker-card">
      <table className="data-table tracker-grid">
        <thead>
          <tr>
            <th className="site-cell">Site</th>
            {columns.map((column) => (
              <th key={column.key} className={column.kind === "money" ? "num" : undefined} title={column.key}>
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((record, index) => (
            <tr key={`${record[WORKS_ORDER] ?? ""}-${index}`}>
              <td className="site-cell">
                <span className="site-name" title={record[SITE_NAME] ?? undefined}>
                  {record[SITE_NAME] ?? "—"}
                </span>
                <span className="sub mono">{record[WORKS_ORDER] ?? "—"}</span>
              </td>
              {columns.map((column) => (
                <td key={column.key} className={cellClass(column, record[column.key])}>
                  {cellText(column, record[column.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td className="site-cell">Totals · {records.length} rows</td>
            {columns.map((column) => (
              <td key={column.key} className={column.kind === "money" ? "num" : undefined}>
                {column.kind === "money" ? formatMoney(sumMoney(records.map((record) => record[column.key]))) : ""}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
